import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Download, ArrowRight, Mail, CheckCircle } from 'lucide-react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resources = [
  {
    id: 'guia-transicion',
    title: 'Guía rápida: Transición a alimentación natural',
    description: 'Los pasos esenciales para cambiar la dieta de tu perro o gato de forma segura.',
    image: '/assets/recursos/formulario.webp',
    type: 'PDF',
    link: '/guia-transicion',
    isExternal: false,
  },
  {
    id: 'checklist-compra',
    title: 'Checklist de compra',
    description: 'Lista organizada de ingredientes básicos para empezar con dieta natural.',
    image: '/assets/recursos/check.webp',
    type: 'PDF',
    placeholder: true,
  },
  {
    id: 'plantilla-registro',
    title: 'Plantilla de registro de comidas y heces',
    description: 'Hoja de seguimiento para monitorizar la tolerancia y evolución de tu animal.',
    image: '/assets/recursos/tabla.webp',
    type: 'Excel/PDF',
    placeholder: true,
  },
];

export const RecursosPage = () => {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!consent) {
      toast.error('Debes aceptar la política de privacidad');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setIsSubscribed(true);
        toast.success('¡Suscripción completada!');
      } else {
        throw new Error('Error al suscribir');
      }
    } catch (error) {
      toast.error('Error al suscribirse. Inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Recursos gratuitos | REALimenta"
        description="Guías y plantillas gratuitas para mejorar la alimentación de tu perro o gato con criterio y practicidad."
        canonical="/recursos"
      />

      <section className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[{ label: 'Recursos' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mb-16"
          >
            <h1 
              className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A2E22] tracking-tighter mb-6"
              data-testid="recursos-h1"
            >
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, #3D6B4F 0%, #8AAE7F 100%)' }}
              >
                Recursos
              </span>
              {' '}gratuitos
            </h1>
            <p className="text-text-secondary text-lg md:text-xl">
              Materiales prácticos para ayudarte a mejorar la alimentación de tu perro o gato.
            </p>
          </motion.div>

          {/* Resources Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {resources.map((resource, index) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                data-testid={`resource-${resource.id}`}
              >
                <div className="bg-white rounded-2xl p-5 flex flex-col h-full shadow-sm border border-gray-100
                              transition-all duration-300 ease-out
                              hover:border-[#3D6B4F] hover:shadow-lg hover:shadow-[#3D6B4F]/10
                              active:bg-[#3D6B4F] active:border-[#3D6B4F]
                              group-hover:scale-[1.02]">
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-[#EDF2EA] p-2 mb-4
                                transition-all duration-300 group-hover:bg-[#E8C84B]/20 group-active:bg-white/20">
                    <img 
                      src={resource.image} 
                      alt={resource.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-contain"
                    />
                  </div>
                
                <span className="text-xs font-medium text-brand-accent uppercase tracking-wide mb-2
                             transition-colors duration-300 group-active:text-[#E8C84B]">
                  {resource.type}
                </span>
                
                <h3 className="font-manrope text-lg font-bold text-text-primary mb-2
                             transition-colors duration-300 group-hover:text-[#3D6B4F] group-active:text-white">
                  {resource.title}
                </h3>
                
                <p className="text-text-secondary text-sm mb-6 flex-1
                            transition-colors duration-300 group-active:text-white/80">
                  {resource.description}
                </p>
                
                {resource.placeholder ? (
                  <span className="text-text-muted text-sm italic transition-colors duration-300 group-active:text-white/60">
                    Próximamente disponible
                  </span>
                ) : resource.isExternal === false ? (
                  <Link
                    to={resource.link}
                    className="inline-flex items-center gap-2 text-brand-primary font-semibold group/link
                             transition-colors duration-300 group-active:text-white"
                  >
                    Ver recurso
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                ) : (
                  <button className="inline-flex items-center gap-2 text-brand-primary font-semibold
                                   transition-colors duration-300 group-active:text-white">
                    <Download className="w-4 h-4" />
                    Descargar
                  </button>
                )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Newsletter Subscription */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-brand-primary rounded-3xl p-8 md:p-12 max-w-3xl mx-auto"
          >
            <div className="text-center mb-8">
              <Mail className="w-12 h-12 text-brand-secondary mx-auto mb-4" />
              <h2 className="font-manrope text-2xl md:text-3xl font-bold text-white mb-4">
                Recibe más recursos
              </h2>
              <p className="text-white/80">
                Suscríbete para recibir guías, consejos y novedades sobre nutrición natural.
              </p>
            </div>

            {isSubscribed ? (
              <div className="text-center" data-testid="newsletter-success">
                <CheckCircle className="w-12 h-12 text-brand-secondary mx-auto mb-4" />
                <p className="text-white font-semibold">¡Gracias por suscribirte!</p>
                <p className="text-white/80 text-sm">Revisa tu bandeja de entrada.</p>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-4" data-testid="newsletter-form">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="tu@email.com"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-secondary"
                  data-testid="newsletter-email"
                />
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="newsletter-consent"
                    checked={consent}
                    onCheckedChange={setConsent}
                    className="border-white/30 data-[state=checked]:bg-brand-secondary data-[state=checked]:border-brand-secondary"
                    data-testid="newsletter-consent"
                  />
                  <Label htmlFor="newsletter-consent" className="text-sm text-white/80 leading-relaxed cursor-pointer">
                    Acepto la{' '}
                    <Link to="/privacidad" className="underline">política de privacidad</Link>
                    {' '}y recibir comunicaciones de REALimenta.
                  </Label>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-secondary"
                  data-testid="newsletter-submit"
                >
                  {isSubmitting ? 'Suscribiendo...' : 'Suscribirme'}
                </button>
              </form>
            )}
          </motion.div>

          {/* Collaboration placeholder */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <h3 className="font-manrope text-xl font-bold text-text-primary mb-4">
              Colaboraciones
            </h3>
            <p className="text-text-muted text-sm max-w-xl mx-auto">
              ¿Tienes un blog, podcast o medio sobre mascotas y te gustaría colaborar? 
              <Link to="/contacto" className="text-brand-primary hover:underline ml-1">
                Escríbeme
              </Link>.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default RecursosPage;
